import { useEffect, useState } from 'react';
import api from '../../services/api';

// Registering the academy's iPad.
//
// Done once per device by an admin. The code is shown exactly once — after
// that only its hash is stored, so it can be revoked but never re-read. That is
// the right trade for a credential that opens the instructor portal, and it is
// why the screen makes such a point of "copy this now".
//
// "New code" exists because once is the plan and not always the outcome: iOS
// can clear a web app's storage, and the home-screen icon gets a storage
// container separate from Safari's. Before it, recovering meant registering a
// second device for the same iPad. Issuing a code leaves the working device
// working — nothing changes until something pairs with it.

const FIELD = 'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';

export default function DeviceManager() {
  const [devices, setDevices] = useState([]);
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/devices');
      setDevices(data.devices);
    } catch {
      setError('Could not load registered devices');
    }
  };

  useEffect(() => { load(); }, []);

  const register = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/devices', { label: label.trim() });
      setIssued(data);
      setLabel('');
      load();
    } catch (registerError) {
      setError(registerError.response?.data?.error || 'Could not register this device');
    } finally {
      setBusy(false);
    }
  };

  const reissue = async (device) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post(`/devices/${device.id}/pairing-code`);
      setIssued(data);
      load();
    } catch (reissueError) {
      setError(reissueError.response?.data?.error || 'Could not issue a new code');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (device) => {
    setBusy(true);
    try {
      await api.delete(`/devices/${device.id}`);
      load();
    } catch {
      setError('Could not revoke that device');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <h2 className="text-lg font-semibold text-gray-900">Instructor iPad</h2>
      <p className="mb-4 mt-1 text-sm text-gray-600">
        Register the wall-mounted iPad once. Instructors then open{' '}
        <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">/instructor</code>{' '}
        and tap their name — no login, ever.
      </p>

      {issued && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
          <p className="text-sm font-medium text-amber-900">
            On the iPad, open <code className="rounded bg-white px-1.5 py-0.5">/instructor</code> and type:
          </p>
          <div className="my-3 flex justify-center gap-2">
            {issued.pairingCode.split('').map((char, index) => (
              <span
                key={index}
                className="flex h-14 w-11 items-center justify-center rounded-lg bg-white font-mono text-2xl font-bold text-gray-900 ring-1 ring-amber-300"
              >
                {char}
              </span>
            ))}
          </div>
          <p className="text-xs text-amber-800">
            Expires in {issued.expiresInMinutes} minutes and works once. The iPad
            then keeps its own key — this code is not needed again. Type it into
            the home-screen icon if you have added one, not into Safari: iOS
            keeps the two apart.
          </p>
          <button
            onClick={() => setIssued(null)}
            className="mt-3 rounded-md bg-amber-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800"
          >
            Done
          </button>
        </div>
      )}

      <form onSubmit={register} className="mb-4 flex gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Academy front wall iPad"
          className={FIELD}
        />
        <button
          type="submit"
          disabled={busy || !label.trim()}
          className="shrink-0 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Register
        </button>
      </form>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <div className="space-y-2">
        {devices.length === 0 && (
          <p className="text-sm text-gray-500">No devices registered yet.</p>
        )}
        {devices.map((device) => (
          <div
            key={device.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-gray-900">
                {device.label}
                {device.revoked && <span className="ml-2 text-xs text-red-600">revoked</span>}
              </div>
              <div className="text-xs text-gray-500">
                {device.pairingExpired && (
                  <span className="text-amber-700">Code expired before it was used · </span>
                )}
                {device.pairingPending && (
                  <span className="text-amber-700">
                    {device.paired ? 'New code waiting to be typed in' : 'Waiting to be set up'} ·{' '}
                  </span>
                )}
                {device.lastSeenAt
                  ? `Last used ${new Date(device.lastSeenAt).toLocaleString()}`
                  : 'Never used'}
                {device.registeredBy && ` · registered by ${device.registeredBy}`}
              </div>
            </div>
            {!device.revoked && (
              <div className="flex shrink-0 gap-2">
                <button
                  onClick={() => reissue(device)}
                  disabled={busy}
                  title="If the iPad ever asks to be set up again"
                  className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  New code
                </button>
                <button
                  onClick={() => revoke(device)}
                  disabled={busy}
                  className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  Revoke
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
